import apiService from "./api";
import { sanitizeQueryParams } from "../utility/helpers/apiHelper";

export interface DashboardAnalyticsParams {
  periodType?: string;
  role?: string;
  region?: string;
  categoryId?: string;
  serviceType?: string;
  ageBand?: string;
}

export interface DashboardSummaryMetrics {
  activeUsers: number;
  bookingsToday: number;
  revenueYtd: number;
  totalBusinesses: number;
}

export interface DashboardChartSeries {
  labels: string[];
  userGrowth: number[];
  revenue: number[];
}

export interface DashboardLabelValue {
  label: string;
  value: number;
}

export interface DashboardTopService {
  name: string;
  revenue: number;
}

export interface DashboardHeatmapPoint {
  x: string;
  y: number;
}

export interface DashboardHeatmapSeries {
  name: string;
  data: DashboardHeatmapPoint[];
}

export interface DashboardActivityHeatmap {
  days: string[];
  hours: string[];
  series: DashboardHeatmapSeries[];
}

export interface DashboardAnalyticsResponse {
  summary: DashboardSummaryMetrics;
  charts: DashboardChartSeries;
  paymentMethods: DashboardLabelValue[];
  topServices: DashboardTopService[];
  activityHeatmap: DashboardActivityHeatmap;
}

export interface DashboardBookingsSummary {
  completed: number;
  upcoming: number;
  cancelled: number;
}

export interface DashboardTransactionsSummary {
  daily: number;
  monthly: number;
}

export interface DashboardUsersByRoleSummary {
  clients: number;
  businesses: number;
  admins: number;
}

export interface DashboardSummary {
  bookings: DashboardBookingsSummary;
  productsSold: number;
  activeOrders: number;
  transactions: DashboardTransactionsSummary;
  usersByRole: DashboardUsersByRoleSummary;
}

export interface DashboardApiResponse {
  status: boolean;
  message: string;
  data: {
    summary: DashboardSummary;
  };
}

export const mapDashboardSummary = (raw: any): DashboardSummary => {
  return {
    bookings: {
      completed: Number(raw?.bookings?.completed ?? 0),
      upcoming: Number(raw?.bookings?.upcoming ?? 0),
      cancelled: Number(raw?.bookings?.cancelled ?? 0),
    },
    productsSold: Number(raw?.productsSold ?? 0),
    activeOrders: Number(raw?.activeOrders ?? 0),
    transactions: {
      daily: Number(raw?.transactions?.daily ?? 0),
      monthly: Number(raw?.transactions?.monthly ?? 0),
    },
    usersByRole: {
      clients: Number(raw?.usersByRole?.clients ?? 0),
      businesses: Number(raw?.usersByRole?.businesses ?? 0),
      admins: Number(raw?.usersByRole?.admins ?? 0),
    },
  };
};

export class DashboardService {
  async getAnalytics(params?: DashboardAnalyticsParams) {
    return apiService.get("/dashboard/analytics", {
      params: sanitizeQueryParams(params || {}),
    });
  }

  async getDashboardData(params?: DashboardAnalyticsParams) {
    return this.getAnalytics(params);
  }
}

export const dashboardService = new DashboardService();
